import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import toast from 'react-hot-toast';
import * as Icons from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import {
    useTheme,
    Box,
    Typography,
    Button,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Container,
    Grid,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Divider,
    Stack,
    Skeleton,
} from "@mui/material";
import api from 'state/axios';
import ScrollToTop from 'components/ScrollToTop';
import PublicAppBar from "components/PublicAppBar";


/* ─── Animation variants ────────────────────────────────────────────────── */

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show:   { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } },
};
const stagger = {
    hidden: {},
    show:   { transition: { staggerChildren: 0.08 } },
};

/* ─── Dynamic icon ──────────────────────────────────────────────────────── */

const DynIcon = ({ name, ...props }) => {
    const Icon = Icons[name] || Icons.Circle;
    return <Icon {...props} />;
};

/* ─── Dot-grid background ───────────────────────────────────────────────── */

const DotGrid = ({ mode }) => (
    <Box sx={{
        position: "absolute", inset: 0, zIndex: -1,
        backgroundImage: `radial-gradient(circle, ${mode === "dark" ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"} 1px, transparent 1px)`,
        backgroundSize: "28px 28px",
        maskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, #000 60%, transparent 100%)",
    }} />
);

/* ─── Section Components ─────────────────────────────────────────────────── */

const SectionBadge = ({ label }) => (
    <Chip
        label={label}
        size="small"
        variant="outlined"
        sx={{
            p: 2,
            mb: 2.5,
            borderRadius: 1.5,
            fontWeight: 600,
            fontSize: "0.7rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
        }}
    />
);

const HeroSection = ({ id, data, mode, navigate }) => {
    const theme = useTheme();
    const lines = (data.headline || "").split("\n");
    const accentLine = lines.pop();
    const normalLines = lines;

    return (
        <Box
            id={id}
            sx={{
                pt: { xs: 14, md: 18 },
                pb: { xs: 10, md: 14 },
                position: "relative",
                overflow: "hidden",
                textAlign: "center",
            }}
        >
            <DotGrid mode={mode} />
            <Box sx={{
                position: "absolute",
                top: "20%",
                left: "50%",
                transform: "translateX(-50%)",
                width: 600,
                height: 300,
                borderRadius: "50%",
                background: `radial-gradient(ellipse, ${alpha(theme.palette.primary.main, mode === "dark" ? 0.15 : 0.1)} 0%, transparent 70%)`,
                zIndex: -1,
                filter: "blur(40px)",
            }} />
            <Container maxWidth="md">
                <motion.div initial="hidden" animate="show" variants={stagger}>
                    <motion.div variants={fadeUp}>
                        {data.badge && <SectionBadge label={data.badge} />}
                    </motion.div>

                    <motion.div variants={fadeUp}>
                        <Typography
                            component="h1"
                            sx={{
                                fontWeight: 600,
                                fontSize: { xs: "2rem", md: "2.5rem", lg: "3rem" },
                                lineHeight: 1.08,
                                letterSpacing: "-0.02em",
                                mb: 0,
                            }}
                        >
                            {normalLines.map((line, i) => (
                                <span key={i}>{line}<br /></span>
                            ))}
                            <Box
                                component="span"
                                sx={{
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 90%)`,
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                {accentLine}
                            </Box>
                        </Typography>
                    </motion.div>

                    {data.subheadline && (
                        <motion.div variants={fadeUp}>
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{
                                    mt: 3,
                                    mb: 5,
                                    mx: "auto",
                                    fontSize: { xs: "1rem", md: "1.1rem" },
                                    lineHeight: 1.75,
                                }}
                            >
                                {data.subheadline}
                            </Typography>
                        </motion.div>
                    )}

                    {data.ctaLabel && (
                        <motion.div variants={fadeUp}>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="center">
                                <Button
                                    variant="contained"
                                    size="large"
                                    endIcon={<Icons.ArrowForward />}
                                    onClick={() => navigate(data.ctaLink || '/contact')}
                                    sx={{ px: 4, py: 1, borderRadius: 2, fontWeight: 600, fontSize: "0.8rem" }}
                                >
                                    {data.ctaLabel}
                                </Button>
                            </Stack>
                        </motion.div>
                    )}
                </motion.div>
            </Container>
        </Box>
    );
};

const StatsSection = ({ id, data }) => {
    const theme = useTheme();
    const items = data.items || [];

    return (
        <Box id={id} sx={{ py: { xs: 5, md: 7 } }}>
            <Container maxWidth="lg">
                <Box sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "center",
                    justifyContent: "center",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                }}>
                    {items.map((stat, i) => (
                        <motion.div
                            key={stat.id || i}
                            style={{ flex: 1 }}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true }}
                            variants={fadeUp}
                            transition={{ delay: i * 0.1 }}
                        >
                            <Box sx={{
                                flex: 1,
                                py: { xs: 3, md: 4 },
                                px: 3,
                                textAlign: "center",
                                borderRight: i < items.length - 1
                                    ? { xs: "none", sm: `1px solid ${theme.palette.divider}` }
                                    : "none",
                                borderBottom: i < items.length - 1
                                    ? { xs: `1px solid ${theme.palette.divider}`, sm: "none" }
                                    : "none",
                            }}>
                                <Typography
                                    variant="h3"
                                    sx={{ fontWeight: 600, color: "primary.main", lineHeight: 1, fontSize: { xs: "2rem", md: "2.5rem" } }}
                                >
                                    {stat.value}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
                                    {stat.label}
                                </Typography>
                            </Box>
                        </motion.div>
                    ))}
                </Box>
            </Container>
        </Box>
    );
};

const FeaturesSection = ({ id, data, label }) => {
    const theme = useTheme();
    const items = (data.items || []).filter(f => f.isVisible !== false);

    return (
        <Box id={id} sx={{ py: { xs: 10, md: 16 } }}>
            <Container maxWidth="lg">
                <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
                    <motion.div variants={fadeUp}>
                        <Box sx={{ textAlign: "center", mb: 8 }}>
                            <SectionBadge label={label || "Features"} />
                            <Typography
                                variant="h3"
                                sx={{ fontWeight: 600, letterSpacing: "-0.02em", mb: 2 }}
                            >
                                {data.headline}
                            </Typography>
                            {data.subheadline && (
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ maxWidth: 540, mx: "auto", lineHeight: 1.7 }}
                                >
                                    {data.subheadline}
                                </Typography>
                            )}
                        </Box>
                    </motion.div>

                    <Grid container spacing={2}>
                        {items.map((feat, i) => (
                            <Grid
                                key={feat.id || i}
                                sx={{
                                    width: { xs: "100%", md: "calc(100% / 3 - 16px)" },
                                    minWidth: { xs: "100%", md: 0 },
                                }}
                            >
                                <motion.div variants={fadeUp} style={{ height: "100%" }}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            height: "100%",
                                            borderRadius: 2,
                                            transition: "border-color 0.2s, box-shadow 0.2s, transform 0.2s",
                                            "&:hover": {
                                                borderColor: "primary.main",
                                                boxShadow: `0 4px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                                                transform: "translateY(-2px)",
                                            },
                                        }}
                                    >
                                        <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1.5 }}>
                                            <Box sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: "10px",
                                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "primary.main",
                                            }}>
                                                <DynIcon name={feat.icon} fontSize="small" />
                                            </Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                                {feat.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                                {feat.description}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>
            </Container>
        </Box>
    );
};

const PricingSection = ({ id, data, label, navigate }) => {
    const theme = useTheme();

    return (
        <Box id={id} sx={{ py: { xs: 10, md: 16 } }}>
            <Container maxWidth="lg">
                <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
                    <motion.div variants={fadeUp}>
                        <Box sx={{ textAlign: "center", mb: 8 }}>
                            <SectionBadge label={label || "Pricing"} />
                            <Typography
                                variant="h3"
                                sx={{ fontWeight: 600, letterSpacing: "-0.02em", mb: 2 }}
                            >
                                {data.headline}
                            </Typography>
                            {data.subheadline && (
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ maxWidth: 480, mx: "auto" }}
                                >
                                    {data.subheadline}
                                </Typography>
                            )}
                        </Box>
                    </motion.div>

                    <Grid container spacing={2} justifyContent="center">
                        {data.tiers?.map((tier, i) => (
                            <Grid
                                key={tier.id || i}
                                sx={{
                                    width: { xs: "100%", md: "30%" },
                                    minWidth: 260,
                                }}
                            >
                                <motion.div variants={fadeUp} style={{ height: "100%" }}>
                                    <Card
                                        variant={tier.isHighlighted ? "elevation" : "outlined"}
                                        elevation={tier.isHighlighted ? 6 : 0}
                                        sx={{
                                            height: "100%",
                                            borderRadius: 2,
                                            position: "relative",
                                            ...(tier.isHighlighted && {
                                                border: `1px solid ${theme.palette.primary.main}`,
                                                background: alpha(theme.palette.primary.main, 0.04),
                                            }),
                                        }}
                                    >
                                        {tier.isHighlighted && (
                                            <Chip
                                                label="Most Popular"
                                                size="small"
                                                color="primary"
                                                sx={{
                                                    zIndex: 3,
                                                    position: "absolute",
                                                    top: 0,
                                                    left: "50%",
                                                    transform: "translateX(-50%)",
                                                    borderRadius: 1,
                                                    fontWeight: 600,
                                                }}
                                            />
                                        )}
                                        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2, p: 3 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 600 }}>{tier.name}</Typography>
                                            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                                                {tier.price != null ? (
                                                    <>
                                                        <Typography variant="h3" sx={{ fontWeight: 600, lineHeight: 1 }}>${tier.price}</Typography>
                                                        <Typography variant="caption" color="text.secondary">/ {tier.unit}</Typography>
                                                    </>
                                                ) : (
                                                    <Typography variant="h5" sx={{ fontWeight: 600, color: "text.secondary" }}>{tier.unit}</Typography>
                                                )}
                                            </Box>
                                            <Divider />
                                            <Box sx={{ flexGrow: 1 }}>
                                                {tier.features?.map((f, j) => (
                                                    <Box key={j} sx={{ display: "flex", alignItems: "flex-start", mb: 1, gap: 1 }}>
                                                        <Icons.Check fontSize="small" sx={{ color: "primary.main", mt: 0.1, flexShrink: 0 }} />
                                                        <Typography variant="body2">{f}</Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                            {tier.isHighlighted && (
                                                <Button
                                                    variant="contained"
                                                    fullWidth
                                                    onClick={() => navigate("/contact")}
                                                    sx={{ borderRadius: 2, fontWeight: 600, mt: 1 }}
                                                >
                                                    Get Started
                                                </Button>
                                            )}
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>
            </Container>
        </Box>
    );
};

const FAQSection = ({ id, data, label }) => (
    <Box id={id} sx={{ py: { xs: 10, md: 16 } }}>
        <Container maxWidth="lg">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
                <motion.div variants={fadeUp}>
                    <Box sx={{ textAlign: "center", mb: 8 }}>
                        <SectionBadge label={label || "FAQ"} />
                        <Typography
                            variant="h3"
                            sx={{ fontWeight: 600, letterSpacing: "-0.02em" }}
                        >
                            {data.headline || "Frequently Asked Questions"}
                        </Typography>
                    </Box>
                </motion.div>
                <Box sx={{ maxWidth: 720, mx: "auto" }}>
                    {data.items?.filter(q => q.isVisible !== false).map((faq, i) => (
                        <motion.div key={faq.id || i} variants={fadeUp}>
                            <Accordion
                                disableGutters
                                elevation={0}
                                sx={{
                                    mb: 1,
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: "12px !important",
                                    "&:before": { display: "none" },
                                    overflow: "hidden",
                                }}
                            >
                                <AccordionSummary expandIcon={<Icons.ExpandMore />} sx={{ px: 3, py: 0.5 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600, py: 0.5 }}>
                                        {faq.question}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 3, pb: 2.5 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                        {faq.answer}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        </motion.div>
                    ))}
                </Box>
            </motion.div>
        </Container>
    </Box>
);

const CTASection = ({ id, data, label, navigate }) => {
    const theme = useTheme();

    return (
        <Box
            id={id}
            sx={{
                py: { xs: 10, md: 8 },
                mx: { xs: 2, md: 4 },
                mb: { xs: 4, md: 6 },
                borderRadius: 2,
                overflow: "hidden",
                position: "relative",
                background: `linear-gradient(135deg, ${alpha(theme.palette.blue.main, 0.8)} 0%, ${alpha(theme.palette.primary.main, 0.9)} 100%)`,
            }}
        >
            <Box sx={{
                position: "absolute", inset: 0, zIndex: 0,
                backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)",
                backgroundSize: "24px 24px",
            }} />
            <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
                <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
                    <motion.div variants={fadeUp}>
                        <Box sx={{ textAlign: "center" }}>
                            {label && (
                                <Chip
                                    label={label}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        mb: 2.5,
                                        borderColor: "rgba(255,255,255,0.4)",
                                        color: "rgba(255,255,255,0.85)",
                                        borderRadius: 1.5,
                                        fontWeight: 600,
                                        fontSize: "0.7rem",
                                        letterSpacing: "0.08em",
                                        textTransform: "uppercase",
                                    }}
                                />
                            )}
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: 600,
                                    mb: 2.5,
                                    color: "#fff",
                                    letterSpacing: "-0.02em",
                                    fontSize: { xs: "2rem", md: "2.8rem" },
                                }}
                            >
                                {data.headline}
                            </Typography>
                            {data.body && (
                                <Typography variant="body1" sx={{ mb: 5, color: "rgba(255,255,255,0.8)", maxWidth: 520, mx: "auto", lineHeight: 1.75 }}>
                                    {data.body}
                                </Typography>
                            )}
                            {data.buttonLabel && (
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate(data.buttonLink || "/contact")}
                                    sx={{
                                        px: 4,
                                        py: 1,
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        bgcolor: "slate.main",
                                        color: "text.primary",
                                        "&:hover": { bgcolor: "slate.mid" },
                                    }}
                                >
                                    {data.buttonLabel}
                                </Button>
                            )}
                        </Box>
                    </motion.div>
                </motion.div>
            </Container>
        </Box>
    );
};

const RichTextSection = ({ id, data }) => (
    <Box id={id} sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
            <Box dangerouslySetInnerHTML={{ __html: data.html || "" }} />
        </Container>
    </Box>
);

const FooterSection = ({ id, data }) => (
    <Box id={id} component="footer" sx={{ borderTop: 1, borderColor: "divider", py: 5 }}>
        <Container maxWidth="lg">
            <Box sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
            }}>
                <Typography variant="body2" color="text.disabled">{data.copyright}</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
                    {data.links?.map(link => (
                        <Button
                            key={link.id || link.label}
                            href={link.url}
                            size="small"
                            sx={{ color: "text.disabled", fontSize: "0.8rem", "&:hover": { color: "text.secondary" } }}
                        >
                            {link.label}
                        </Button>
                    ))}
                </Stack>
            </Box>
        </Container>
    </Box>
);

/* ─── Loading Skeleton ───────────────────────────────────────────────────── */

const PageSkeleton = () => (
    <Box sx={{ pt: 14 }}>
        <Container maxWidth="md" sx={{ textAlign: "center", pb: 10 }}>
            <Skeleton variant="rounded" width={220} height={28} sx={{ mx: "auto", mb: 3, borderRadius: 1.5 }} />
            <Skeleton variant="text" width="65%" height={80} sx={{ mx: "auto" }} />
            <Skeleton variant="text" width="50%" height={80} sx={{ mx: "auto", mb: 3 }} />
            <Skeleton variant="text" width="75%" height={24} sx={{ mx: "auto", mb: 1 }} />
            <Skeleton variant="text" width="65%" height={24} sx={{ mx: "auto", mb: 5 }} />
            <Skeleton variant="rounded" width={200} height={52} sx={{ mx: "auto", borderRadius: 2 }} />
        </Container>
        <Divider />
        <Container maxWidth="lg" sx={{ py: 6 }}>
            <Skeleton variant="rounded" height={90} sx={{ borderRadius: 2 }} />
        </Container>
        <Container maxWidth="lg" sx={{ py: 6 }}>
            <Skeleton variant="text" width="35%" height={44} sx={{ mx: "auto", mb: 6 }} />
            <Grid container spacing={2}>
                {[...Array(6)].map((_, i) => (
                    <Grid key={i} width="calc(100% / 3)">
                        <Skeleton variant="rounded" height={160} sx={{ borderRadius: 2 }} />
                    </Grid>
                ))}
            </Grid>
        </Container>
    </Box>
);

/* ─── Main Page ──────────────────────────────────────────────────────────── */

export default function HomePage() {
    const navigate = useNavigate();
    const mode = useSelector(state => state.mode);

    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setMounted(true);
        const handleScroll = () => setIsScrolled(window.scrollY > 10);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        api.get('/homepageconfig')
            .then(res => setConfig(res.data))
            .catch((err) => toast.error("Failed to load home page!", err))
            .finally(() => setLoading(false));
    }, []);

    const sections = useMemo(
        () => config?.sections ? [...config.sections].sort((a, b) => a.order - b.order) : [],
        [config]
    );

    const navItems = useMemo(() => [
        ...sections
            .filter(s => s.showInNav && s.isVisible)
            .map(s => ({ label: s.label, anchor: s.id, isVisible: true })),
        { label: "Charts", path: "/flightgraph", isVisible: true },
        { label: "Sign In", path: "/auth/login", isVisible: false },
    ], [sections]);

    const hasFooter = useMemo(
        () => sections.some(s => s.type === "footer" && s.isVisible),
        [sections]
    );

    const renderSection = useCallback((section) => {
        if (!section.isVisible) return null;
        const d = section.content || {};
        const lbl = section.label;
        switch (section.type) {
            case "hero":     return <HeroSection     id={section.id} key={section.id} data={d} mode={mode} navigate={navigate} />;
            case "stats":    return <StatsSection    id={section.id} key={section.id} data={d} />;
            case "features": return <FeaturesSection id={section.id} key={section.id} data={d} label={lbl} />;
            case "pricing":  return <PricingSection  id={section.id} key={section.id} data={d} label={lbl} navigate={navigate} />;
            case "faq":      return <FAQSection      id={section.id} key={section.id} data={d} label={lbl} />;
            case "cta":      return <CTASection      id={section.id} key={section.id} data={d} label={lbl} navigate={navigate} />;
            case "richtext": return <RichTextSection id={section.id} key={section.id} data={d} />;
            case "footer":   return <FooterSection   id={section.id} key={section.id} data={d} />;
            default: return null;
        }
    }, [mode, navigate]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
            <PublicAppBar
                mounted={mounted}
                isScrolled={isScrolled}
                onOpenMobileMenu={() => setMobileMenuOpen(true)}
                menuItems={navItems}
            />

            <Drawer
                anchor="right"
                open={mobileMenuOpen}
                onClose={() => setMobileMenuOpen(false)}
                sx={{ "& .MuiDrawer-paper": { width: 240 } }}
            >
                <Box sx={{ py: 2, px: 1 }}>
                    <List dense>
                        {navItems.filter(i => i.isVisible).map(item => (
                            <ListItem key={item.label} disablePadding>
                                <ListItemButton
                                    sx={{ borderRadius: 2 }}
                                    onClick={() => {
                                        setMobileMenuOpen(false);
                                        if (item.path) navigate(item.path);
                                        else document.getElementById(item.anchor)?.scrollIntoView({ behavior: "smooth" });
                                    }}
                                >
                                    <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 500 }} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ px: 1, pb: 1 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                                navigate("/auth/login");
                                setMobileMenuOpen(false);
                            }}
                            sx={{ borderRadius: 2, mb: 1 }}
                        >
                            Sign In
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            <Box component="main" sx={{ flex: 1 }}>
                {loading ? (
                    <PageSkeleton />
                ) : sections.length === 0 ? (
                    <Box sx={{ py: 24, textAlign: "center" }}>
                        <Icons.AirplanemodeInactive sx={{ fontSize: 56, color: "text.disabled", mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                            This page is being set up.
                        </Typography>
                    </Box>
                ) : (
                    <AnimatePresence>{sections.map(renderSection)}</AnimatePresence>
                )}
            </Box>

            {!loading && !hasFooter && (
                <Box component="footer" sx={{ borderTop: 1, borderColor: "divider", py: 4 }}>
                    <Container>
                        <Typography variant="body2" color="text.disabled" textAlign="center">
                            © {new Date().getFullYear()} DigitAir. All rights reserved.
                        </Typography>
                    </Container>
                </Box>
            )}

            <ScrollToTop />
        </Box>
    );
}
